/**
 * Bot kong decisions — split out of `BotDriver.ts` for the 200-line cap. Both
 * entry points are pure: given state (+ context), return the resulting
 * state, or `null` when no kong applies. `BotDriver` owns the timing/hooks;
 * these functions own only "what does declaring this kong actually do".
 */

import {
  claimExposedKong,
  declareAddedKong,
  declareConcealedKong,
  type MahjongState,
} from '../game/GameState.js';
import { addedKongAvailable, concealedKongOptions, type SeatClaims } from '../game/Claims.js';
import type { Seat } from '../game/types.js';
import type { AiStrategy, KongDecision } from '../ai/types.js';

/** Every concealed/added kong `seat` may declare on its own turn — mirrors
 *  `EasyStrategy.chooseKong`'s own-turn logic, but collects every option
 *  instead of picking one (used to surface the local seat's kong buttons). */
export function ownTurnKongOptions(state: MahjongState, seat: Seat): KongDecision[] {
  const options: KongDecision[] = concealedKongOptions(state.hands[seat] ?? [], state.ruleset).map(
    (tile) => ({ kind: 'concealed', tile }) as const,
  );
  if (
    state.drawnTile !== null &&
    addedKongAvailable(state.melds[seat] ?? [], state.drawnTile, state.ruleset)
  ) {
    options.push({ kind: 'added', tileId: state.drawnTile.id });
  }
  return options;
}

/** Applies a bot's own-turn kong decision (concealed or added). */
export function applyOwnTurnKong(state: MahjongState, seat: Seat, kong: KongDecision): MahjongState {
  switch (kong.kind) {
    case 'concealed':
      return declareConcealedKong(state, seat, kong.tile);
    case 'added':
      return declareAddedKong(state, seat, kong.tileId);
    case 'exposed':
      throw new Error("unreachable: 'exposed' only occurs during a claim window");
  }
}

/**
 * Applies a bot's exposed-kong claim on the open discard, if one wants it.
 * Exposed kong isn't in the `Claim` union, so it can't flow through
 * `resolveClaimPriority` — a kong can't lose to a same-discard Pung/Chi from
 * another seat (holding 3 for kong vs. fewer for pung/chi of the same tile
 * is mutually exclusive), but it CAN lose to a Ron, so callers must only use
 * this when no seat's `SeatClaims.canRon` is true (checked here too, as a
 * guard). Returns the resulting state, or `null` when no bot exposed-konged.
 */
export function tryBotExposedKong(
  seatClaims: readonly SeatClaims[],
  state: MahjongState,
  strategy: AiStrategy,
): MahjongState | null {
  if (seatClaims.some((c) => c.canRon)) return null;
  for (const claims of seatClaims) {
    if (!claims.canKong) continue;
    const kong = strategy.chooseKong(claims.seat, state);
    if (kong !== null && kong.kind === 'exposed') return claimExposedKong(state, claims.seat);
  }
  return null;
}
